import React, { useState } from 'react';
import { PlusIcon, ServerStackIcon, ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, TrashIcon, UserGroupIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import BackendForm from './BackendForm';
import { HAProxyBackend, HAProxyServer } from '../../types';
import { HAProxyBackendStats } from '../../types/stats';
import { backendsApi } from '../../services/api';

type DisplayHAProxyServer = HAProxyServer & {
  status?: string;
  currentSessions?: number;
  bytesIn?: number;
  bytesOut?: number;
};

interface BackendsListProps {
  backends: HAProxyBackend[];
  stats?: HAProxyBackendStats[]; // Optional live stats
  onAddBackend: (backend: Omit<HAProxyBackend, 'id' | 'instanceId'> & { servers: HAProxyServer[] }) => void;
  onEditBackend: (backend: HAProxyBackend) => void;
  onDeleteBackend: (id: string) => void;
}

const BackendsList: React.FC<BackendsListProps> = ({
  backends,
  stats,
  onAddBackend,
  onEditBackend,
  onDeleteBackend
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBackends, setExpandedBackends] = useState<Record<string, boolean>>({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBackend, setEditingBackend] = useState<HAProxyBackend | null>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteBackendName, setDeleteBackendName] = useState<string>('');
  
  // Use config backends as the source of truth, merge in stats if available
  const displayBackends = backends.map(configBackend => {
    // Find matching stats backend by name
    const statsBackend = stats?.find(s => s.name.trim().toLowerCase() === configBackend.name.trim().toLowerCase());
    // Merge stats into config servers by name
    const servers = (configBackend.servers || []).map(configServer => {
      const statsServer = statsBackend?.servers.find(
        s => s.name.trim().toLowerCase() === configServer.name.trim().toLowerCase()
      );
      return {
        ...configServer,
        ...(statsServer || {}), // Merge stats fields directly into the server object
      };
    });
    return {
      ...configBackend,
      ...(statsBackend || {}), // Merge stats fields directly into the backend object
      servers,
    };
  });

  const filteredBackends = displayBackends.filter(backend => 
    backend.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleBackendExpanded = async (backendId: string) => {
    // Create a copy of the current state 
    const newExpandedState = { ...expandedBackends };
    
    // Toggle only the selected backend
    const isExpanding = !newExpandedState[backendId];
    newExpandedState[backendId] = isExpanding;
    
    setExpandedBackends(newExpandedState);
    
    // If expanding and we don't have servers yet, fetch them
    if (isExpanding) {
      const backend = backends.find(b => b.id === backendId);
      if (backend && (!backend.servers || backend.servers.length === 0)) {
        try {
          setLoading(prev => ({ ...prev, [backendId]: true }));
          const backendWithServers = await backendsApi.getById(backendId);
          
          // Update the backend with its servers
          onEditBackend({
            ...backendWithServers
          });
        } catch (error) {
          console.error('Failed to fetch servers for backend:', error);
        } finally {
          setLoading(prev => ({ ...prev, [backendId]: false }));
        }
      }
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-zinc-500'; // gray for no status
    switch (status.toUpperCase()) {
      case 'UP':
        return 'bg-green-500';
      case 'DOWN':
        return 'bg-red-500';
      case 'MAINT':
      case 'MAINTENANCE':
        return 'bg-yellow-500';
      case 'DRAIN':
        return 'bg-blue-500';
      default:
        return 'bg-orange-500'; // orange for any other status
    }
  };

  const handleAddBackend = () => {
    setEditingBackend(null);
    setIsFormOpen(true);
  };

  const handleEditBackend = (backend: HAProxyBackend) => {
    setEditingBackend(backend);
    setIsFormOpen(true);
  };

  const handleDeleteBackend = (id: string, name: string) => {
    setConfirmDelete(id);
    setDeleteBackendName(name);
  };

  const confirmDeleteBackend = (id: string) => {
    onDeleteBackend(id);
    setConfirmDelete(null);
  };

  const handleSaveBackend = (backend: Omit<HAProxyBackend, 'id' | 'instanceId'> & { id?: string, servers: HAProxyServer[] }) => {
    if (editingBackend) {
      // Update existing backend
      onEditBackend({
        ...backend,
        id: editingBackend.id,
        serverCount: backend.servers.length
      } as HAProxyBackend);
    } else {
      // Add new backend
      onAddBackend({
        ...backend,
        servers: backend.servers
      });
    }
    
    setIsFormOpen(false);
    setEditingBackend(null);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingBackend(null);
  };

  // Add a helper to format bytes
  function formatBytes(bytes: number): string {
    if (bytes > 1e6) return (bytes / 1e6).toFixed(1) + ' MB';
    if (bytes > 1e3) return (bytes / 1e3).toFixed(1) + ' KB';
    return bytes + ' B';
  }

  return (
    <div className="space-y-6">
      {/* Backend form modal */}
      <BackendForm 
        backend={editingBackend}
        onSave={handleSaveBackend}
        onCancel={handleCancelForm}
        isOpen={isFormOpen}
      />
      
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-zinc-100 flex items-center">
          <ServerStackIcon className="h-6 w-6 mr-2 text-indigo-500" />
          HAProxy Backends
        </h2>
        <button
          type="button"
          onClick={handleAddBackend}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New Backend
        </button>
      </div>
      
      {/* Description */}
      <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-300">
        <p>Backends define groups of servers that HAProxy distributes requests to. Each backend can specify its load balancing algorithm and health check settings.</p>
        <p className="mt-2">Click on a backend row to expand and see all servers in that backend.</p>
      </div>
      
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search backends..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-md bg-zinc-800 border border-zinc-700 px-4 py-2 text-zinc-200 focus:outline-none focus:border-indigo-500"
        />
      </div>

      {/* Backends list */}
      {displayBackends.length === 0 ? (
        <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-8 text-center">
          <ServerStackIcon className="mx-auto h-12 w-12 text-zinc-500" />
          <h3 className="mt-2 text-sm font-medium text-zinc-400">No backends found</h3>
          <p className="mt-1 text-sm text-zinc-500">Get started by creating a new backend.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleAddBackend}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New Backend
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/30">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-800/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Balance</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Servers</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider"></th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 bg-transparent">
              {filteredBackends.map((backend) => {
                // Find the config backend for edit/delete actions
                const configBackend = backends.find(b => b.name === backend.name);
                const backendId = configBackend?.id || backend.name;
                const backendBalance = configBackend?.balance || '';
                return (
                  <React.Fragment key={backendId}>
                    <tr className={`hover:bg-zinc-800/30 transition-colors duration-150 ${expandedBackends[backendId] ? 'bg-zinc-800/30' : ''}`}> 
                      {/* Name with status dot and arrow */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <button 
                            onClick={() => toggleBackendExpanded(backendId)}
                            className="mr-2 text-zinc-400 hover:text-indigo-400 focus:outline-none"
                          >
                            {expandedBackends[backendId] ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                          <span className={`inline-block align-middle mr-2 rounded-full w-3 h-3 ${getStatusColor((backend.status ?? '').trim().toUpperCase())}`}></span>
                          <div 
                            className="text-sm font-medium text-zinc-100 hover:text-indigo-400 cursor-pointer"
                            onClick={() => toggleBackendExpanded(backendId)}
                          >
                            {backend.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                        <div>{backendBalance}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                        <div>{(configBackend?.servers ? configBackend.servers.length : 0)} servers</div>
                      </td>
                      {/* Stats - right-aligned, no actions here */}
                      <td className="px-0 py-4 whitespace-nowrap text-sm text-zinc-300 w-full">
                        <div className="flex justify-end items-center space-x-2">
                          <span title="Current Sessions" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                            <UserGroupIcon className="h-4 w-4 mr-1 text-indigo-400" />
                            <span className="font-bold">{backend.currentSessions ?? '-'}</span>
                          </span>
                          <span title="Bytes In" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1 text-green-400" />
                            <span className="font-bold">{formatBytes(backend.bytesIn ?? 0)}</span>
                          </span>
                          <span title="Bytes Out" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                            <ArrowUpTrayIcon className="h-4 w-4 mr-1 text-pink-400" />
                            <span className="font-bold">{formatBytes(backend.bytesOut ?? 0)}</span>
                          </span>
                        </div>
                      </td>
                      {/* Actions - now in their own cell */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => configBackend && handleEditBackend(configBackend)}
                            className="text-indigo-400 hover:text-indigo-300 transition-colors duration-150"
                            title="Edit Backend"
                            disabled={!configBackend}
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => configBackend && handleDeleteBackend(configBackend.id, configBackend.name)}
                            className="text-red-400 hover:text-red-300 transition-colors duration-150"
                            title="Delete Backend"
                            disabled={!configBackend}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedBackends[backendId] && (
                      <tr>
                        <td colSpan={5} className="px-0 py-0 border-b border-zinc-800">
                          <div className="bg-zinc-800/30 px-6 py-4">
                            <h3 className="text-sm font-medium text-zinc-200 mb-2">Servers</h3>
                            <div className="overflow-x-auto mb-6">
                              {loading[backendId] ? (
                                <div className="text-center py-4">
                                  <div className="spinner mb-2"></div>
                                  <p className="text-sm text-zinc-400">Loading servers...</p>
                                </div>
                              ) : (
                                <table className="min-w-full divide-y divide-zinc-700/50">
                                  <thead className="">
                                    <tr>
                                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Server</th>
                                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Address</th>
                                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Port</th>
                                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Weight</th>
                                      <th scope="col" className="px-0 py-2 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">Stats</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-zinc-700/50">
                                    {(backend.servers || []).map((server, index) => {
                                      const displayServer = server as DisplayHAProxyServer;
                                      const normalizedStatus = (displayServer.status ?? '').trim().toUpperCase();
                                      return (
                                        <tr key={`server-${backendId}-${index}-${displayServer.name}`} className="hover:bg-zinc-800/50">
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <div className="flex items-center">
                                              <span className={`inline-block align-middle mr-2 rounded-full w-3 h-3 ${getStatusColor(normalizedStatus)}`}></span>
                                              <div className="text-sm font-medium text-zinc-200">{displayServer.name}</div>
                                            </div>
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <div className="text-sm text-zinc-300">{displayServer.address}</div>
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <div className="text-sm text-zinc-300">{displayServer.httpPort}</div>
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap">
                                            <div className="text-sm text-zinc-300">{displayServer.weight}</div>
                                          </td>
                                          <td className="px-0 py-2 whitespace-nowrap text-sm text-zinc-300 w-full">
                                            <div className="flex justify-end items-center space-x-2">
                                              <span title="Current Sessions" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                                                <UserGroupIcon className="h-4 w-4 mr-1 text-indigo-400" />
                                                <span className="font-bold">{displayServer.currentSessions ?? '-'}</span>
                                              </span>
                                              <span title="Bytes In" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                                                <ArrowDownTrayIcon className="h-4 w-4 mr-1 text-green-400" />
                                                <span className="font-bold">{formatBytes(displayServer.bytesIn ?? 0)}</span>
                                              </span>
                                              <span title="Bytes Out" className="inline-flex items-center bg-zinc-800 rounded-full px-3 py-1 text-xs font-semibold text-zinc-200 shadow-sm">
                                                <ArrowUpTrayIcon className="h-4 w-4 mr-1 text-pink-400" />
                                                <span className="font-bold">{formatBytes(displayServer.bytesOut ?? 0)}</span>
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                    {(!configBackend?.servers || configBackend.servers.length === 0) && (
                                      <tr>
                                        <td colSpan={5} className="px-4 py-3 text-center text-sm text-zinc-400">
                                          No servers found in this backend.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10">
            <div className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-3">Confirm Delete</h3>
              <p className="text-sm text-zinc-300 mb-6">
                Are you sure you want to delete the backend "{deleteBackendName}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteBackend(confirmDelete)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendsList; 